import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams, Navigate } from "react-router-dom";
import { Save, Download, Upload, Plus, Trash2, Map as MapIcon, RefreshCw, AlertCircle, Check, FileJson, FileCode, FileSpreadsheet, LogOut } from "lucide-react";
import type { Plot, PlotsResponse, PlotStatus } from "../types";
import { isAdminAuthed, clearAdminAuth } from "./AdminLandingPage";

/* ───────────────────────── CSV helpers ───────────────────────── */
/** Minimal RFC4180-ish CSV parser supporting quoted fields, commas, and newlines inside quotes. */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cur = "";
  let i = 0;
  let inQuotes = false;
  const src = text.replace(/\r\n/g, "\n");
  while (i < src.length) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cur += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      cur += ch;
      i += 1;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (ch === ",") {
      row.push(cur);
      cur = "";
      i += 1;
      continue;
    }
    if (ch === "\n") {
      row.push(cur);
      rows.push(row);
      row = [];
      cur = "";
      i += 1;
      continue;
    }
    cur += ch;
    i += 1;
  }
  row.push(cur);
  if (row.length > 1 || row[0] !== "") rows.push(row);
  return rows;
}

function toCsv(rows: (string | number)[][]): string {
  return rows
    .map((r) =>
      r
        .map((cell) => {
          const s = cell === undefined || cell === null ? "" : String(cell);
          if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
          return s;
        })
        .join(",")
    )
    .join("\n");
}

/* ───────────────────────── Constants ───────────────────────── */
const CSV_HEADERS = [
  "Villa Number",
  "Plot Number",
  "Area",
  "Villa Type",
  "Island",
  "Waterfront Length",
  "Pool Type",
  "Bedrooms",
  "Bathrooms",
  "Status",
] as const;

const STATUSES: PlotStatus[] = ["available", "reserved", "sold"];

type ImportPreview = {
  toUpdate: { existing: Plot; patch: Partial<Plot> }[];
  toAdd: Plot[];
  skippedRows: number;
  warnings: string[];
};

/* ───────────────────────── Page ───────────────────────── */
export default function TableAdminPage() {
  const [searchParams] = useSearchParams();
  const adminRequested = searchParams.get("admin") === "1";

  // Gate behind the /admin login session.
  if (!adminRequested || !isAdminAuthed()) {
    return <Navigate to="/admin" replace />;
  }
  const isAdmin = adminRequested;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [data, setData] = useState<PlotsResponse | null>(null);
  const [plots, setPlots] = useState<Plot[]>([]);
  const [villaTypes, setVillaTypes] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  /* ── Load data ── */
  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch("/api/plots");
        if (!res.ok) throw new Error("Failed to load plots");
        const json = (await res.json()) as PlotsResponse;
        if (!ok) return;
        setData(json);
        setPlots(json.plots ?? []);
        setVillaTypes(json.villaTypes ?? []);
      } catch (e) {
        if (ok) setError(e instanceof Error ? e.message : "Failed to load");
      } finally {
        if (ok) setLoading(false);
      }
    })();
    return () => {
      ok = false;
    };
  }, []);

  /* ── Helpers ── */
  const islandOptions = useMemo(() => data?.islands ?? [], [data]);
  const islandLabel = (id?: string) => islandOptions.find((i) => i.id === id)?.label ?? "";
  const islandIdByLabel = (label: string) => {
    const norm = label.trim().toLowerCase();
    return islandOptions.find((i) => i.label.toLowerCase() === norm || i.id.toLowerCase() === norm)?.id;
  };

  const poolTypeSuggestions = useMemo(() => {
    const set = new Set<string>();
    plots.forEach((p) => p.poolType && set.add(p.poolType));
    return Array.from(set);
  }, [plots]);

  /* ── Editing ── */
  const updatePlotField = <K extends keyof Plot>(id: string, field: K, value: Plot[K]) => {
    setPlots((prev) => prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)));
    setDirty(true);
  };

  const addPlot = () => {
    const id = `plot-${Date.now()}`;
    const next: Plot = {
      id,
      label: String(plots.length + 1),
      name: `Villa ${plots.length + 1}`,
      type: villaTypes[0] ?? "",
      status: "available",
      x: 0.5,
      y: 0.5,
      blueprintSrc: "",
      areaSqft: 0,
      bedrooms: 0,
      bathrooms: 0,
      description: "",
      mapsUrl: "",
    };
    setPlots((prev) => [...prev, next]);
    setDirty(true);
  };

  const deletePlot = (id: string) => {
    if (!window.confirm("Delete this row? This cannot be undone before save.")) return;
    setPlots((prev) => prev.filter((p) => p.id !== id));
    setDirty(true);
  };

  /* ── Save ── */
  const save = async () => {
    if (!isAdmin || !data) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch("/api/plots", {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ plots, villaTypes }),
      });
      if (!res.ok) throw new Error("Could not save");
      const json = (await res.json()) as PlotsResponse;
      setData(json);
      setPlots(json.plots ?? []);
      setVillaTypes(json.villaTypes ?? villaTypes);
      setDirty(false);
      setNotice("Saved successfully");
      setTimeout(() => setNotice(null), 2200);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  /* ── Generic file download helper ── */
  const downloadFile = (filename: string, content: string, mime: string) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const todayTag = () => new Date().toISOString().slice(0, 10);

  /* ── CSV Export ── */
  const exportCsv = () => {
    const rows: (string | number)[][] = [
      [...CSV_HEADERS],
      ...plots.map((p) => [
        p.label ?? "",
        p.plotNumber ?? "",
        p.areaSqft ?? 0,
        p.type ?? "",
        islandLabel(p.islandId),
        p.waterfrontLength ?? "",
        p.poolType ?? "",
        p.bedrooms ?? 0,
        p.bathrooms ?? 0,
        p.status ?? "available",
      ]),
    ];
    downloadFile(`villas-${todayTag()}.csv`, toCsv(rows), "text/csv;charset=utf-8;");
  };

  /* ── Full DB JSON export (everything from /api/plots) ── */
  const exportDbJson = async () => {
    setError(null);
    try {
      const res = await fetch("/api/plots");
      if (!res.ok) throw new Error("Failed to fetch database");
      const json = await res.json();
      downloadFile(`masterplan-db-${todayTag()}.json`, JSON.stringify(json, null, 2), "application/json;charset=utf-8;");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Backup failed");
    }
  };

  /* ── Schema export (describes the JSON shape) ── */
  const exportSchema = () => {
    const schema = {
      $schema: "http://json-schema.org/draft-07/schema#",
      title: "Immense Estate Masterplan DB",
      type: "object",
      properties: {
        overviewImage: { type: "object", properties: { src: { type: "string" }, width: { type: "number" }, height: { type: "number" } } },
        islandImage: { type: "object", properties: { src: { type: "string" }, width: { type: "number" }, height: { type: "number" } } },
        diamondPosition: { type: "object", properties: { x: { type: "number" }, y: { type: "number" } } },
        overviewDiamonds: { type: "array", items: { type: "object" } },
        overviewIslandLabels: { type: "array", items: { type: "object" } },
        islands: { type: "array", items: { type: "object" } },
        poiCategories: { type: "array", items: { type: "object" } },
        pois: { type: "array", items: { type: "object" } },
        islandTextLabels: { type: "array", items: { type: "object" } },
        villaTypes: { type: "array", items: { type: "string" } },
        mapsUrl: { type: "string" },
        plots: {
          type: "array",
          items: {
            type: "object",
            required: ["id", "label", "status", "x", "y"],
            properties: {
              id: { type: "string", description: "Stable unique id" },
              label: { type: "string", description: "Villa Number (visible label)" },
              name: { type: "string" },
              type: { type: "string", description: "Villa Type (e.g. Villa A)" },
              status: { type: "string", enum: ["available", "reserved", "sold"] },
              x: { type: "number", minimum: 0, maximum: 1, description: "Normalized X position on island image" },
              y: { type: "number", minimum: 0, maximum: 1, description: "Normalized Y position on island image" },
              blueprintSrc: { type: "string" },
              areaSqft: { type: "number" },
              bedrooms: { type: "number" },
              bathrooms: { type: "number" },
              description: { type: "string" },
              mapsUrl: { type: "string" },
              plotNumber: { type: "string", description: "Plot Number (separate from label)" },
              islandId: { type: "string", description: "References IslandConfig.id" },
              waterfrontLength: { type: "number" },
              poolType: { type: "string" },
              villaFloors: { type: "array", items: { type: "object" } },
              roomLabels: { type: "array", items: { type: "object" } },
              floorPlans: { type: "array", items: { type: "object" } },
              typeOptions: { type: "array", items: { type: "object" } },
            },
          },
        },
      },
    };
    downloadFile(`masterplan-schema-${todayTag()}.json`, JSON.stringify(schema, null, 2), "application/json;charset=utf-8;");
  };

  /* ── CSV Import — builds a preview; user confirms before applying ── */
  const importCsv = (file: File) => {
    setError(null);
    setNotice(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result ?? "");
        const rows = parseCsv(text).filter((r) => r.some((c) => c.trim() !== ""));
        if (!rows.length) throw new Error("CSV is empty");
        const header = rows[0].map((h) => h.trim().toLowerCase());
        const idx = (name: string) => header.indexOf(name.toLowerCase());
        const cVilla = idx("Villa Number");
        const cPlot = idx("Plot Number");
        const cArea = idx("Area");
        const cType = idx("Villa Type");
        const cIsland = idx("Island");
        const cWaterfront = idx("Waterfront Length");
        const cPool = idx("Pool Type");
        const cBed = idx("Bedrooms");
        const cBath = idx("Bathrooms");
        const cStatus = idx("Status");
        if (cVilla === -1) throw new Error("Missing required 'Villa Number' column");

        const byLabel = new Map<string, Plot>();
        plots.forEach((p) => byLabel.set(String(p.label).trim().toLowerCase(), p));

        const toUpdate: { existing: Plot; patch: Partial<Plot> }[] = [];
        const toAdd: Plot[] = [];
        const warnings: string[] = [];
        let skippedRows = 0;

        const seen = new Set<string>();
        for (let r = 1; r < rows.length; r++) {
          const row = rows[r];
          const villaNum = (row[cVilla] ?? "").trim();
          if (!villaNum) {
            skippedRows += 1;
            continue;
          }
          const key = villaNum.toLowerCase();
          if (seen.has(key)) {
            warnings.push(`Row ${r + 1}: duplicate Villa Number "${villaNum}" — last entry will win.`);
          }
          seen.add(key);
          const existing = byLabel.get(key);
          const numOrUndef = (s: string) => (s.trim() === "" ? undefined : Number(s) || 0);
          const islandRaw = cIsland >= 0 ? (row[cIsland] ?? "").trim() : "";
          if (cIsland >= 0 && islandRaw && !islandIdByLabel(islandRaw)) {
            warnings.push(`Row ${r + 1}: unknown island "${islandRaw}" — left unchanged.`);
          }
          const patch: Partial<Plot> = {
            label: villaNum,
            plotNumber: cPlot >= 0 ? (row[cPlot] ?? "").trim() : existing?.plotNumber,
            areaSqft: cArea >= 0 ? Number((row[cArea] ?? "").trim()) || 0 : existing?.areaSqft,
            type: cType >= 0 ? (row[cType] ?? "").trim() || existing?.type : existing?.type,
            islandId: cIsland >= 0 ? (islandIdByLabel(islandRaw) ?? existing?.islandId) : existing?.islandId,
            waterfrontLength: cWaterfront >= 0 ? numOrUndef(row[cWaterfront] ?? "") ?? existing?.waterfrontLength : existing?.waterfrontLength,
            poolType: cPool >= 0 ? (row[cPool] ?? "").trim() || existing?.poolType : existing?.poolType,
            bedrooms: cBed >= 0 ? numOrUndef(row[cBed] ?? "") ?? existing?.bedrooms ?? 0 : existing?.bedrooms,
            bathrooms: cBath >= 0 ? numOrUndef(row[cBath] ?? "") ?? existing?.bathrooms ?? 0 : existing?.bathrooms,
            status: cStatus >= 0 ? normalizeStatus((row[cStatus] ?? "").trim()) ?? existing?.status ?? "available" : existing?.status ?? "available",
          };

          if (existing) {
            toUpdate.push({ existing, patch });
          } else {
            toAdd.push({
              id: `plot-${Date.now()}-${r}`,
              label: villaNum,
              name: `Villa ${villaNum}`,
              type: patch.type ?? "",
              status: patch.status ?? "available",
              x: 0.5,
              y: 0.5,
              blueprintSrc: "",
              areaSqft: patch.areaSqft ?? 0,
              bedrooms: patch.bedrooms ?? 0,
              bathrooms: patch.bathrooms ?? 0,
              description: "",
              mapsUrl: "",
              plotNumber: patch.plotNumber,
              islandId: patch.islandId,
              waterfrontLength: patch.waterfrontLength,
              poolType: patch.poolType,
            });
          }
        }

        if (!toUpdate.length && !toAdd.length) {
          throw new Error("No valid rows found in CSV");
        }
        setPreview({ toUpdate, toAdd, skippedRows, warnings });
      } catch (e) {
        setError(e instanceof Error ? e.message : "Import failed");
      }
    };
    reader.onerror = () => setError("Could not read file");
    reader.readAsText(file);
  };

  const applyPreview = () => {
    if (!preview) return;
    const updates: Plot[] = plots.map((p) => {
      const found = preview.toUpdate.find((u) => u.existing.id === p.id);
      // Existing plots keep id, x, y, blueprint, floors, etc. — only patch fields are merged.
      return found ? ({ ...p, ...found.patch } as Plot) : p;
    });
    const merged = [...updates, ...preview.toAdd];
    setPlots(merged);
    setDirty(true);
    setNotice(`CSV applied — ${preview.toUpdate.length} updated, ${preview.toAdd.length} added. Click Update to save.`);
    setPreview(null);
  };

  function normalizeStatus(s: string): PlotStatus | null {
    const v = s.toLowerCase();
    if (v === "available" || v === "reserved" || v === "sold") return v;
    return null;
  }

  const logout = () => {
    clearAdminAuth();
    window.location.href = "/admin";
  };

  void isAdmin;

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy-950 via-navy-900 to-navy-950 text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-20 border-b border-white/[0.06] bg-navy-950/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 text-navy-950">
              <span className="text-base font-black">A</span>
            </div>
            <div>
              <div className="text-[10px] font-semibold tracking-[0.2em] text-amber-400 uppercase">Table Admin</div>
              <div className="text-sm font-bold text-white">Durrat Al Bahrain — Plot Management</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-[11px] text-white/60">Welcome <b className="text-white">Administrator</b></span>
            <Link
              to="/masterplan?admin=1"
              className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] font-semibold text-white/80 hover:bg-white/10"
              title="Switch to interactive (map) admin"
            >
              <MapIcon className="h-3.5 w-3.5" /> Map Admin
            </Link>
            <button
              onClick={logout}
              className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] font-semibold text-white/80 hover:bg-white/10"
              title="Sign out"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 py-8">
        {/* Action bar */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-bold tracking-wide">Villas</h1>
            <div className="mt-0.5 text-[12px] text-white/55">
              {plots.length} villa{plots.length === 1 ? "" : "s"} · positions are preserved automatically
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={addPlot}
              className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-[11px] font-semibold text-white/80 transition hover:bg-white/10"
            >
              <Plus className="h-3.5 w-3.5" /> Add Row
            </button>
            <button
              onClick={save}
              disabled={!dirty || saving}
              className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[11px] font-bold tracking-wide shadow-lg transition ${
                !dirty || saving
                  ? "bg-amber-500/30 text-amber-200/60 cursor-not-allowed"
                  : "bg-gradient-to-r from-amber-400 to-amber-500 text-navy-950 hover:brightness-110 shadow-amber-500/20"
              }`}
              title={dirty ? "Save changes" : "No unsaved changes"}
            >
              {saving ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              {saving ? "Saving…" : "Update"}
            </button>
            <button
              onClick={exportCsv}
              className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-4 py-1.5 text-[11px] font-bold tracking-wide text-navy-950 hover:brightness-110 shadow-lg shadow-amber-500/20 transition"
            >
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-4 py-1.5 text-[11px] font-bold tracking-wide text-navy-950 hover:brightness-110 shadow-lg shadow-amber-500/20 transition"
            >
              <Upload className="h-3.5 w-3.5" /> Import CSV
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) importCsv(f);
                e.target.value = "";
              }}
            />
          </div>
        </div>

        {/* Notices */}
        {error && (
          <div className="mb-3 flex items-center gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-200">
            <AlertCircle className="h-3.5 w-3.5" /> {error}
          </div>
        )}
        {notice && (
          <div className="mb-3 flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-[12px] text-emerald-200">
            <Check className="h-3.5 w-3.5" /> {notice}
          </div>
        )}
        {dirty && !error && !notice && (
          <div className="mb-3 flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[12px] text-amber-200">
            <AlertCircle className="h-3.5 w-3.5" /> Unsaved changes — click Update to persist.
          </div>
        )}

        {/* Table */}
        <div className="overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.02] shadow-2xl shadow-black/30">
          <div className="overflow-x-auto">
            <table className="min-w-full text-[12px]">
              <thead>
                <tr className="bg-gradient-to-r from-amber-500/90 to-amber-400/90 text-navy-950">
                  {CSV_HEADERS.map((h) => (
                    <th key={h} className="whitespace-nowrap px-4 py-3 text-left text-[11px] font-bold tracking-wider uppercase">
                      {h}
                    </th>
                  ))}
                  <th className="whitespace-nowrap px-3 py-3 text-right text-[11px] font-bold tracking-wider uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={CSV_HEADERS.length + 1} className="px-4 py-10 text-center text-white/50">
                      Loading…
                    </td>
                  </tr>
                ) : plots.length === 0 ? (
                  <tr>
                    <td colSpan={CSV_HEADERS.length + 1} className="px-4 py-10 text-center text-white/50">
                      No villas yet. Click <b>Add Row</b> or <b>Import CSV</b>.
                    </td>
                  </tr>
                ) : (
                  plots.map((p, i) => (
                    <tr key={p.id} className={i % 2 === 0 ? "bg-white/[0.02]" : "bg-white/[0.04]"}>
                      <td className="px-4 py-2">
                        <input
                          value={p.label ?? ""}
                          onChange={(e) => updatePlotField(p.id, "label", e.target.value)}
                          className="w-24 rounded-md border border-white/10 bg-navy-950/60 px-2 py-1.5 text-[12px] text-white outline-none focus:border-amber-400"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          value={p.plotNumber ?? ""}
                          onChange={(e) => updatePlotField(p.id, "plotNumber", e.target.value)}
                          className="w-24 rounded-md border border-white/10 bg-navy-950/60 px-2 py-1.5 text-[12px] text-white outline-none focus:border-amber-400"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          value={p.areaSqft ?? 0}
                          onChange={(e) => updatePlotField(p.id, "areaSqft", Number(e.target.value) || 0)}
                          className="w-24 rounded-md border border-white/10 bg-navy-950/60 px-2 py-1.5 text-[12px] text-white outline-none focus:border-amber-400"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <select
                          value={p.type ?? ""}
                          onChange={(e) => updatePlotField(p.id, "type", e.target.value)}
                          className="w-32 rounded-md border border-white/10 bg-navy-950/60 px-2 py-1.5 text-[12px] text-white outline-none focus:border-amber-400"
                        >
                          <option value="">—</option>
                          {villaTypes.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                          {p.type && !villaTypes.includes(p.type) && <option value={p.type}>{p.type}</option>}
                        </select>
                      </td>
                      <td className="px-4 py-2">
                        <select
                          value={p.islandId ?? ""}
                          onChange={(e) => updatePlotField(p.id, "islandId", e.target.value || undefined)}
                          className="w-36 rounded-md border border-white/10 bg-navy-950/60 px-2 py-1.5 text-[12px] text-white outline-none focus:border-amber-400"
                        >
                          <option value="">—</option>
                          {islandOptions.map((i) => (
                            <option key={i.id} value={i.id}>
                              {i.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          value={p.waterfrontLength ?? ""}
                          onChange={(e) => updatePlotField(p.id, "waterfrontLength", e.target.value === "" ? undefined : Number(e.target.value))}
                          className="w-24 rounded-md border border-white/10 bg-navy-950/60 px-2 py-1.5 text-[12px] text-white outline-none focus:border-amber-400"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          list="pool-types"
                          value={p.poolType ?? ""}
                          onChange={(e) => updatePlotField(p.id, "poolType", e.target.value)}
                          className="w-28 rounded-md border border-white/10 bg-navy-950/60 px-2 py-1.5 text-[12px] text-white outline-none focus:border-amber-400"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          min={0}
                          value={p.bedrooms ?? 0}
                          onChange={(e) => updatePlotField(p.id, "bedrooms", Number(e.target.value) || 0)}
                          className="w-20 rounded-md border border-white/10 bg-navy-950/60 px-2 py-1.5 text-[12px] text-white outline-none focus:border-amber-400"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          min={0}
                          value={p.bathrooms ?? 0}
                          onChange={(e) => updatePlotField(p.id, "bathrooms", Number(e.target.value) || 0)}
                          className="w-20 rounded-md border border-white/10 bg-navy-950/60 px-2 py-1.5 text-[12px] text-white outline-none focus:border-amber-400"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <select
                          value={p.status}
                          onChange={(e) => updatePlotField(p.id, "status", e.target.value as PlotStatus)}
                          className={`w-28 rounded-md border px-2 py-1.5 text-[12px] outline-none focus:border-amber-400 ${
                            p.status === "available"
                              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                              : p.status === "reserved"
                              ? "border-amber-500/30 bg-amber-500/10 text-amber-200"
                              : "border-rose-500/30 bg-rose-500/10 text-rose-200"
                          }`}
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s} className="bg-navy-950 text-white">
                              {s.charAt(0).toUpperCase() + s.slice(1)}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => deletePlot(p.id)}
                          className="rounded-md border border-rose-500/30 bg-rose-500/10 p-1.5 text-rose-300 transition hover:bg-rose-500/20"
                          title="Delete row"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <datalist id="pool-types">
          {poolTypeSuggestions.map((p) => (
            <option key={p} value={p} />
          ))}
        </datalist>

        {/* ── Database Backup section ── */}
        <section className="mt-8 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 shadow-xl shadow-black/30">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-sm font-bold tracking-wide text-white">Database Backup</h2>
              <p className="mt-0.5 text-[12px] text-white/55">Download a full backup directly to your computer. Recommended before bulk imports.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={exportDbJson}
                className="flex items-center gap-1.5 rounded-full border border-sky-400/30 bg-sky-500/10 px-3.5 py-1.5 text-[11px] font-semibold text-sky-200 transition hover:bg-sky-500/20"
              >
                <FileJson className="h-3.5 w-3.5" /> Download DB (.json)
              </button>
              <button
                onClick={exportSchema}
                className="flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3.5 py-1.5 text-[11px] font-semibold text-emerald-200 transition hover:bg-emerald-500/20"
              >
                <FileCode className="h-3.5 w-3.5" /> Download Schema (.json)
              </button>
              <button
                onClick={exportCsv}
                className="flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-500/10 px-3.5 py-1.5 text-[11px] font-semibold text-amber-200 transition hover:bg-amber-500/20"
              >
                <FileSpreadsheet className="h-3.5 w-3.5" /> Download CSV
              </button>
            </div>
          </div>
        </section>

        <div className="mt-4 text-[11px] text-white/40 leading-relaxed">
          <b className="text-white/70">Tip:</b> CSV import matches rows by <b>Villa Number</b>. Existing villas keep their map positions, blueprint, floors, and other interactive-admin data. New rows are added with default positions; place them visually using <Link to="/masterplan?admin=1" className="text-amber-300 hover:text-amber-200">Map Admin</Link>.
        </div>
      </main>

      {/* ── CSV Import Preview Modal ── */}
      {preview && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-navy-900 p-6 shadow-2xl">
            <div className="mb-4 flex items-start justify-between">
              <div>
                <div className="text-[10px] font-bold tracking-[0.2em] text-amber-400 uppercase">CSV Import Preview</div>
                <h3 className="mt-1 text-lg font-bold text-white">Review changes before applying</h3>
              </div>
              <button onClick={() => setPreview(null)} className="text-white/50 hover:text-white"><AlertCircle className="hidden" />×</button>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <Stat label="Updated" value={preview.toUpdate.length} accent="sky" />
              <Stat label="New" value={preview.toAdd.length} accent="emerald" />
              <Stat label="Skipped" value={preview.skippedRows} accent="amber" />
            </div>

            {preview.warnings.length > 0 && (
              <div className="mt-4 max-h-32 overflow-y-auto rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
                <div className="text-[11px] font-bold text-amber-300 mb-1">Warnings</div>
                <ul className="space-y-1 text-[11px] text-amber-200/90">
                  {preview.warnings.slice(0, 50).map((w, i) => (
                    <li key={i}>• {w}</li>
                  ))}
                  {preview.warnings.length > 50 && <li className="text-amber-200/60">…and {preview.warnings.length - 50} more</li>}
                </ul>
              </div>
            )}

            <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.02] p-3 text-[11px] text-white/60 leading-relaxed">
              <b className="text-white/80">Merge rules:</b> Existing villas (matched by <b>Villa Number</b>) keep their map position, blueprint, floors, room labels, and any other field not present in the CSV. New villas are added at default position <code className="text-amber-300">(0.5, 0.5)</code> — open Map Admin to place them.
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setPreview(null)}
                className="rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-[11px] font-semibold text-white/80 hover:bg-white/10"
              >
                Cancel
              </button>
              <button
                onClick={applyPreview}
                className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-4 py-1.5 text-[11px] font-bold text-navy-950 shadow-lg shadow-amber-500/20 hover:brightness-110"
              >
                <Check className="h-3.5 w-3.5" /> Apply Import
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent: "sky" | "emerald" | "amber" }) {
  const colorMap = {
    sky: "border-sky-400/30 bg-sky-500/10 text-sky-200",
    emerald: "border-emerald-400/30 bg-emerald-500/10 text-emerald-200",
    amber: "border-amber-400/30 bg-amber-500/10 text-amber-200",
  }[accent];
  return (
    <div className={`rounded-xl border p-3 ${colorMap}`}>
      <div className="text-[10px] font-bold tracking-wider uppercase opacity-80">{label}</div>
      <div className="mt-1 text-2xl font-black">{value}</div>
    </div>
  );
}
